import { Box, Tab, TabList, TabPanel, TabPanels, Tabs } from "@chakra-ui/react";
import { __ } from "@wordpress/i18n";
import React from "react";
import AdvancedControls from "../../../components/common/AdvancedControls";
import InspectorTabs, {
	AdvancedInspectorControls,
	GeneralInspectorControls,
	StyleInspectorControls,
} from "../../../components/common/InspectorTabs";
import Panel from "../../../components/common/Panel";
import ResponsiveControls from "../../../components/common/ResponsiveControls";
import ZStack from "../../../components/common/ZStack";
import {
	Global,
	TextAlignCenter,
	TextAlignLeft,
	TextAlignRight,
} from "../../../components/icons/Icons";
import {
	ColorPicker,
	Input,
	PopoverDrawer,
	Select,
	Slider,
	Toggle,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
} from "../../../controls";
import Reset from "../../../controls/common/Reset";
import { isCSSVariable } from "../../../utils";

type Props = {
	attributes: any;
	setAttributes: (attributes: any) => void;
};

const InspectorControls: React.ComponentType<Props> = (props) => {
	const {
		attributes: {
			blockMargin,
			blockPadding,
			hideOnDesktop,
			hideOnTablet,
			hideOnMobile,
			blockZIndex,
			cssID,
			className,

			homeLabel,
			separator,
			showHomeLink,

			alignment,
			spacing,
			color,
			hoverColor,
			separatorColor,
			typography,
		},
		setAttributes,
	} = props;

	return (
		<>
			<InspectorTabs />
			<GeneralInspectorControls>
				<Panel title={__("Layout", "magazine-blocks")} initialOpen>
					<Input
						label={__("Home Link Label", "magazine-blocks")}
						type="text"
						value={homeLabel}
						onChange={(val: any) =>
							setAttributes({ homeLabel: val })
						}
					/>
					<Select
						options={[
							{ label: "/", value: "/" },
							{ label: ">", value: ">" },
							{ label: "»", value: "»" },
							{ label: "•", value: "•" },
							{ label: "-", value: "-" },
						]}
						onChange={(val) => setAttributes({ separator: val })}
						value={separator}
						label={__("Separator", "magazine-blocks")}
					/>
					<Toggle
						checked={showHomeLink}
						onChange={() =>
							setAttributes({
								showHomeLink: !showHomeLink,
							})
						}
						label={__("Show Home Link", "magazine-blocks")}
					/>
				</Panel>
			</GeneralInspectorControls>
			<StyleInspectorControls>
				<Panel title={__("Properties", "magazine-blocks")} initialOpen>
					<ToggleButtonGroup
						value={alignment}
						onChange={(v) => {
							setAttributes({ alignment: v });
						}}
						label={__("Alignment", "magazine-blocks")}
						resetAttributeKey="alignment"
						groupProps={{
							sx: {
								".chakra-button": {
									minH: "32px",
									"&[data-active]": {
										svg: {
											fill: "#fff !important",
										},
									},
								},
							},
						}}
					>
						<ToggleButton value="left">
							<TextAlignLeft />
						</ToggleButton>
						<ToggleButton value="center">
							<TextAlignCenter />
						</ToggleButton>
						<ToggleButton value="right">
							<TextAlignRight />
						</ToggleButton>
					</ToggleButtonGroup>
					<Slider
						label={__("Spacing Between Items", "magazine-blocks")}
						value={spacing}
						onChange={(val: any) => setAttributes({ spacing: val })}
						min={0}
						max={50}
						step={1}
						units={["px", "rem", "em", "%"]}
						defaultUnit="px"
						responsive
						resetAttributeKey="spacing"
					/>
				</Panel>
				<Panel title={__("Color", "magazine-blocks")}>
					<PopoverDrawer
						label={__("Link Color", "magazine-blocks")}
						trigger={(props) => {
							const Trigger = () => (
								<PopoverDrawer.Trigger {...props}>
									<ZStack
										items={[
											{ color: color, id: "color" },
											{
												color: hoverColor,
												id: "hoverColor",
											},
										]}
									/>
								</PopoverDrawer.Trigger>
							);
							if (
								isCSSVariable(color) ||
								isCSSVariable(hoverColor)
							) {
								return (
									<Box position="relative">
										<Trigger />
										<Global
											position="absolute"
											fill="primary.500"
											height="14px"
											width="14px"
											top="-5px"
											left="-10px"
											bg="white"
											borderRadius="full"
										/>
									</Box>
								);
							}
							return <Trigger />;
						}}
						closeOnFocusOutside
					>
						<Tabs position="relative" size="sm">
							<TabList>
								<Tab>{__("Normal", "magazine-blocks")}</Tab>
								<Tab>{__("Hover", "magazine-blocks")}</Tab>
							</TabList>
							<TabPanels>
								<TabPanel>
									<Reset
										saved={color}
										resetKey="color"
										onReset={(v) => {
											setAttributes({ color: v });
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<ColorPicker
										value={color}
										onChange={(v) =>
											setAttributes({ color: v })
										}
									/>
								</TabPanel>
								<TabPanel>
									<Reset
										saved={hoverColor}
										resetKey="hoverColor"
										onReset={(v) => {
											setAttributes({ hoverColor: v });
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<ColorPicker
										value={hoverColor}
										onChange={(v) =>
											setAttributes({ hoverColor: v })
										}
									/>
								</TabPanel>
							</TabPanels>
						</Tabs>
					</PopoverDrawer>

					<PopoverDrawer
						label={__("Separator Color", "magazine-blocks")}
						trigger={(props) => {
							const Trigger = () => (
								<PopoverDrawer.Trigger {...props}>
									<ZStack
										items={[
											{
												color: separatorColor,
												id: "separatorColor",
											},
										]}
									/>
								</PopoverDrawer.Trigger>
							);
							if (isCSSVariable(separatorColor)) {
								return (
									<Box position="relative">
										<Trigger />
										<Global
											position="absolute"
											fill="primary.500"
											height="14px"
											width="14px"
											top="-5px"
											left="-10px"
											bg="white"
											borderRadius="full"
										/>
									</Box>
								);
							}
							return <Trigger />;
						}}
						closeOnFocusOutside
					>
						<Tabs position="relative">
							<TabList>
								<Tab>{__("Normal", "magazine-blocks")}</Tab>
							</TabList>
							<TabPanels>
								<TabPanel>
									<Reset
										saved={separatorColor}
										resetKey="separatorColor"
										onReset={(v) => {
											setAttributes({
												separatorColor: v,
											});
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<ColorPicker
										value={separatorColor}
										onChange={(v) =>
											setAttributes({ separatorColor: v })
										}
									/>
								</TabPanel>
							</TabPanels>
						</Tabs>
					</PopoverDrawer>
				</Panel>
				<Panel title={__("Typography", "magazine-blocks")}>
					<Typography
						value={typography}
						onChange={(v) =>
							setAttributes({
								typography: v,
							})
						}
						resetAttributeKey="typography"
					/>
				</Panel>
			</StyleInspectorControls>
			<AdvancedInspectorControls>
				<AdvancedControls
					blockMargin={blockMargin}
					setAttributes={setAttributes}
					blockPadding={blockPadding}
				/>
				<ResponsiveControls
					{...{
						hideOnDesktop,
						hideOnTablet,
						hideOnMobile,
						setAttributes,
					}}
				/>
			</AdvancedInspectorControls>
		</>
	);
};

export default InspectorControls;
